import React, { useRef, useEffect } from "react";
import Select from "react-select";
import InternationalRateResultTable from "./InternationalRateResultTable";

export default function InternationalRateCalculatorFormUI({
  form,
  errors,
  loading,
  apiError,
  apiResponse,
  countryOptions,
  selectedCountry,
  handleInputChange,
  handleCountryChange,
  handleSubmit,
}) {
  const resultRef = useRef(null);
  const errorRef = useRef(null); // 👈 New ref for error

  // Function to prevent negative values
  const preventNegative = (e) => {
    if (e.key === '-' || e.key === 'e') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (
      apiResponse &&
      apiResponse.data &&
      Array.isArray(apiResponse.data.data)
    ) {
      resultRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (apiError) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [apiResponse, apiError]); // 👈 Added apiError to dependency array

  return (
    <>
      <div className="bg-white rounded-2xl p-6 md:p-10  border border-indigo-200  duration-500 min-h-[500px] mb-10 w-full">
        <form className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Origin & Delivery Pincode */}
            <div className="col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Pickup Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  name="pickup_pincode"
                  value={form.pickup_pincode}
                  onChange={handleInputChange}
                  onKeyDown={preventNegative}
                  placeholder="Enter pickup pincode"
                  className="w-full border rounded-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                />
                {errors.pickup_pincode && (
                  <div className="text-red-500 text-sm">
                    {errors.pickup_pincode}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">
                  Delivery Pincode
                </label>
                <input
                  type="number"
                  min="0"
                  name="delivery_pincode"
                  value={form.delivery_pincode}
                  onChange={handleInputChange}
                  onKeyDown={preventNegative}
                  placeholder="Enter delivery pincode"
                  className="w-full border rounded-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                />
              </div>
            </div>

            {/* Weight */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Weight (Kg) <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <input
                  type="number"
                  min="0"
                  name="weight"
                  value={form.weight}
                  onChange={handleInputChange}
                  onKeyDown={preventNegative}
                  placeholder="Eg 0.5"
                  className="w-full border rounded-l-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                />
                <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-indigo-300 rounded-r-xl text-black">
                  Kg
                </span>
              </div>
              {errors.weight && (
                <div className="text-red-500 text-sm">{errors.weight}</div>
              )}
            </div>

            {/* Order Amount */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Invoice value <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-indigo-300 rounded-l-xl text-black">
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  name="order_amount"
                  value={form.order_amount}
                  onChange={handleInputChange}
                  onKeyDown={preventNegative}
                  placeholder="Order amount"
                  className="w-full border rounded-r-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
                />
              </div>
              {errors.order_amount && (
                <div className="text-red-500 text-sm">
                  {errors.order_amount}
                </div>
              )}
            </div>

            {/* Shipment Purpose */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Shipment Purpose <span className="text-red-500">*</span>
              </label>
              <select
                name="shipment_purpose"
                value={form.shipment_purpose}
                onChange={handleInputChange}
                className="w-full border rounded-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
              >
                <option value="">Select</option>
                <option value="SCSB4">Sample(CSB4)</option>
                <option value="DCSB4">Document(CSB4)</option>
                <option value="CSB5">Commercial(CSB5)</option>
              </select>
              {errors.shipment_purpose && (
                <div className="text-red-500 text-sm">
                  {errors.shipment_purpose}
                </div>
              )}
            </div>

            {/* Package Type */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Package Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type_of_package"
                value={form.type_of_package}
                onChange={handleInputChange}
                className="w-full border rounded-xl px-4 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition"
              >
                <option>select package</option>
                <option value="SPS">Single Package</option>
                <option value="MPS">Multiple Package</option>
              </select>
            </div>

            {/* Delivery Country */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Delivery Country <span className="text-red-500">*</span>
              </label>
              <Select
                options={countryOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select or search country..."
                classNamePrefix="react-select"
                className=""
                styles={{
                  control: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    color: "black",
                  }),
                }}
              />
              {errors.delivery_country_id && (
                <div className="text-red-500 text-sm">
                  {errors.delivery_country_id}
                </div>
              )}
            </div>

            {/* Dimensions */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Dimensions (in CM) <span className="text-red-500">*</span>
              </label>

              <div className="flex flex-wrap gap-4">
                {form.type_of_package === "MPS" && (
                  <div className="w-32">
                    <input
                      type="number"
                      min="0"
                      name="no_of_box"
                      value={form.dimensions[0].no_of_box}
                      onChange={handleInputChange}
                      onKeyDown={preventNegative}
                      placeholder="No. of Boxes"
                      className="w-full border rounded-lg px-3 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition text-sm"
                    />
                    {errors.no_of_box && (
                      <div className="text-red-500 text-xs">
                        {errors.no_of_box}
                      </div>
                    )}
                  </div>
                )}

                <div className="w-24">
                  <input
                    type="number"
                    min="0"
                    name="height"
                    value={form.dimensions[0].height}
                    onChange={handleInputChange}
                    onKeyDown={preventNegative}
                    placeholder="Height"
                    className="w-full border rounded-lg px-3 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition text-sm"
                  />
                  {errors.height && (
                    <div className="text-red-500 text-xs">{errors.height}</div>
                  )}
                </div>

                <div className="w-24">
                  <input
                    type="number"
                    min="0"
                    name="length"
                    value={form.dimensions[0].length}
                    onChange={handleInputChange}
                    onKeyDown={preventNegative}
                    placeholder="Length"
                    className="w-full border rounded-lg px-3 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition text-sm"
                  />
                  {errors.length && (
                    <div className="text-red-500 text-xs">{errors.length}</div>
                  )}
                </div>

                <div className="w-24">
                  <input
                    type="number"
                    min="0"
                    name="width"
                    value={form.dimensions[0].width}
                    onChange={handleInputChange}
                    onKeyDown={preventNegative}
                    placeholder="Width"
                    className="w-full border rounded-lg px-3 py-2 text-gray-800 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition text-sm"
                  />
                  {errors.width && (
                    <div className="text-red-500 text-xs">{errors.width}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              className="bg-blue-900 hover:bg-blue-800 text-white font-semibold py-2 px-8 rounded-xl shadow-md transition text-base cursor-pointer flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading && (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                  ></path>
                </svg>
              )}
              {loading ? "Calculating..." : "Calculate Rate"}
            </button>
          </div>
        </form>
      </div>

      {apiError && (
        <div
          ref={errorRef} // 👈 Attach errorRef here
          className="mt-4 mx-auto max-w-md border border-red-500 bg-red-100 text-red-700 text-center px-4 py-3 rounded-md shadow-md"
        >
          {apiError}
        </div>
      )}

      <div ref={resultRef}>
        {apiResponse &&
        apiResponse.data &&
        Array.isArray(apiResponse.data.data) ? (
          apiResponse.data.data.length > 0 ? (
            <div className="mt-6">
              <InternationalRateResultTable
                rates={apiResponse.data.data}
                form={form}
              />
            </div>
          ) : (
            <div className="mt-6 text-red-600 font-semibold">
              This country code is not serviceable.
            </div>
          )
        ) : null}
      </div>
    </>
  );
}

